import { Resource } from "@dune/types";
import { Badge, Button } from "@dune/ui";
import { formatSimpleDate } from "@dune/utils";
import { resourceTypeLabels } from "@dune/vars";
import { ColumnDef } from "@tanstack/react-table";
import { Download, FileText, Loader2, Pencil, Trash2 } from "lucide-react";

interface ResourcesColumnsProps {
	onEdit: (resource: Resource) => void;
	onDelete: (resource: Resource) => void;
	onDownload?: (resource: Resource) => void;
	loadingDocumentId?: string | null;
	downloadingDocumentId?: string | null;
}

export const getResourcesColumns = ({
	onEdit,
	onDelete,
	onDownload,
	loadingDocumentId,
	downloadingDocumentId,
}: ResourcesColumnsProps): ColumnDef<Resource>[] => [
	{
		accessorKey: "name",
		header: "Libellé",
		cell: ({ row }) => {
			const resource = row.original;
			const isLoadingDocument = loadingDocumentId === resource.id.toString();

			return (
				<div className="flex items-center gap-3 min-w-0">
					{isLoadingDocument ? (
						<Loader2 size={16} className="animate-spin flex-shrink-0" />
					) : (
						<FileText size={16} className="flex-shrink-0" />
					)}
					<span className="truncate max-w-xs" title={resource.name}>
						{resource.name}
					</span>
				</div>
			);
		},
	},
	{
		accessorKey: "description",
		header: "Description",
		cell: ({ row }) => {
			return <span>{row.original.description ?? "-"}</span>;
		},
	},
	{
		accessorKey: "type",
		header: "Type",
		cell: ({ row }) => {
			return (
				<Badge variant="secondary">
					{resourceTypeLabels[row.original.type]}
				</Badge>
			);
		},
	},
	{
		accessorKey: "theme",
		header: "Thème",
		cell: ({ row }) => {
			return <Badge variant="outline">{row.original.theme.name}</Badge>;
		},
	},
	{
		accessorKey: "updatedAt",
		header: "Dernière modification",
		cell: ({ row }) => {
			return <span>{formatSimpleDate(row.original.updatedAt)}</span>;
		},
	},
	{
		accessorKey: "createdAt",
		header: "Création",
		cell: ({ row }) => {
			return <span>{formatSimpleDate(row.original.createdAt)}</span>;
		},
	},
	{
		id: "actions",
		header: "Actions",
		cell: ({ row }) => {
			const resource = row.original;
			const isDownloadingDocument =
				downloadingDocumentId === resource.id.toString();

			return (
				<div className="flex items-center gap-1 justify-end">
					{onDownload && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="h-8 w-8 p-0"
							aria-label="download"
							disabled={isDownloadingDocument}
							onClick={(event) => {
								event.stopPropagation();
								onDownload(resource);
							}}
						>
							{isDownloadingDocument ? (
								<Loader2 size={14} className="animate-spin" />
							) : (
								<Download size={14} />
							)}
						</Button>
					)}
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={(event) => {
							event.stopPropagation();
							onEdit(resource);
						}}
					>
						<Pencil className="h-4 w-4" />
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={(event) => {
							event.stopPropagation();
							onDelete(resource);
						}}
					>
						<Trash2 className="h-4 w-4 text-red-600" />
					</Button>
				</div>
			);
		},
	},
];

interface ResourcesReadOnlyColumnsProps {
	onDownload: (resource: Resource) => void;
	loadingDocumentId: string | null;
	downloadingDocumentId: string | null;
}

export const getResourcesReadOnlyColumns = ({
	onDownload,
	loadingDocumentId,
	downloadingDocumentId,
}: ResourcesReadOnlyColumnsProps): ColumnDef<Resource>[] => [
	{
		accessorKey: "name",
		header: "Libellé",
		cell: ({ row }) => {
			const resource = row.original;
			const isLoadingDocument = loadingDocumentId === resource.id.toString();

			return (
				<div className="flex items-center gap-3 min-w-0">
					{isLoadingDocument ? (
						<Loader2 size={16} className="animate-spin flex-shrink-0" />
					) : (
						<FileText size={16} className="flex-shrink-0" />
					)}
					<span className="truncate max-w-xs" title={resource.name}>
						{resource.name}
					</span>
				</div>
			);
		},
	},
	{
		accessorKey: "description",
		header: "Description",
		cell: ({ row }) => {
			return <span>{row.original.description ?? "-"}</span>;
		},
	},
	{
		accessorKey: "type",
		header: "Type",
		cell: ({ row }) => {
			return (
				<Badge variant="secondary">
					{resourceTypeLabels[row.original.type]}
				</Badge>
			);
		},
	},
	{
		accessorKey: "theme",
		header: "Thème",
		cell: ({ row }) => {
			return <Badge variant="outline">{row.original.theme.name}</Badge>;
		},
	},
	{
		accessorKey: "updatedAt",
		header: "Dernière modification",
		cell: ({ row }) => {
			return <span>{formatSimpleDate(row.original.updatedAt)}</span>;
		},
	},
	{
		id: "actions",
		header: "Actions",
		cell: ({ row }) => {
			const resource = row.original;
			const isDownloadingDocument =
				downloadingDocumentId === resource.id.toString();

			return (
				<div className="flex items-center gap-1">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-8 w-8 p-0"
						aria-label="download"
						disabled={isDownloadingDocument}
						onClick={(event) => {
							event.stopPropagation();
							onDownload(resource);
						}}
					>
						{isDownloadingDocument ? (
							<Loader2 size={14} className="animate-spin" />
						) : (
							<Download size={14} />
						)}
					</Button>
				</div>
			);
		},
	},
];
